import {Component, Input, ChangeDetectorRef, OnInit} from '@angular/core';
import * as _ from 'lodash';

import { pricingSegmentData } from '../../../segment-data/pricing-segment-data';
import CurrencyCodeMapper from 'utils/currency-code-mapper';

import { BaseScorecardComponent } from '../base/_base-scorecard.component';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { PricingScorecardService } from './pricing-scorecard.service';


@Component({
  moduleId: module.id,
  selector: 'pricing-scorecard',
  templateUrl: 'pricing-scorecard.component.html',
  styleUrls: ['pricing-scorecard.component.scss'],
})
export class PricingScorecardComponent extends BaseScorecardComponent implements OnInit {
  segmentTitle: string = 'Pricing';
  segmentData: any = pricingSegmentData;

  currencyList: Array<any>;
  defaultPriceSheetNumbers: Array<string>;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get segmentScores(): any {
    return _.get(this.scoreData, 'segments.prices.tallies');
  }

  get pricingScorecardTitle(): string {
    return `${this.pricingScorecardService.selectedPriceSheetName} Price List Scorecard`;
  }

  get selectedPriceSheetNumber(): string {
    return this.pricingScorecardService.selectedPriceSheetNumber;
  }

  get activePriceTypes(): Array<SegmentFieldItem> {
    return this.pricingScorecardService.activePriceTypes;
  }

  get isDefaultPriceSheetSelected(): boolean {
    return this.isDefaultPriceSheet(this.pricingScorecardService.selectedPriceSheetNumber);
  }

  get primaryPriceSheetNumber(): string {
    return this.pricingScorecardService.primaryPriceSheetNumber;
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    private pricingScorecardService: PricingScorecardService,
  ) {
    super(ref, scorecardConnectorService);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.currencyList = CurrencyCodeMapper.getCurrencyList();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onCurrencyItemExpand(item: SegmentFieldItem, currencyCode: string) {
    this.scorecardConnectorService.scoreDetailsExpanded.emit({item, currencyCode});
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * $event: {item: SegmentFieldItem}
   */
  protected handleControlFocus($event: any) {
    if (!this.isDefaultPriceSheetSelected) { return; }
    this.handleNestedControlFocus($event);
  }

  /*
    * data: {populated, status, item: SegmentFieldItem}
   */
  protected getItemFieldScoreFromEvent(data: any) {
    return this.getScore(data.item.item_name, data.currencyCode);
  }

  protected findScorecardItemViaData(data: any): ScorecardItemComponent {
    return this.findScorecardItem('fieldMap', data.item.item_name);
  }

  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any) {
    this.collapseExpandedChildScores(scorecardItem.scorecardChildItems, data.currencyCode);
  }

  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    return this.getChildScorecardItemByName(scorecardItem, data.currencyCode);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private isDefaultPriceSheet(priceSheetNumber: string): boolean {
    return this.primaryPriceSheetNumber === priceSheetNumber;
  }

  private isPriceTypeValid(item: SegmentFieldItem): boolean {
    return _.every(this.currencyList, (currency: any) => {
      return this.isScoreValid(item, currency.code) === 1;
    });
  }

  private isScoreValid(item: SegmentFieldItem, currencyCode: string): number {
    const score = this.getScore(item.item_name, currencyCode);
    return _.get(score, 'valid', 1);
  }

  private isScorePopulated(item: SegmentFieldItem, currencyCode: string): number {
    const score = this.getScore(item.item_name, currencyCode);
    return _.get(score, 'populated');
  }

  private getScore(priceType: string, currencyCode: string): any {
    return _.get(this.segmentScores, `${this.selectedPriceSheetNumber}.${priceType}.${currencyCode}.price`);
  }

  private getPriceType(item: SegmentFieldItem): any {
    return _.get(this.segmentScores, `${this.selectedPriceSheetNumber}.${item.item_name}`);
  }

  private getPriceSheet(): any {
    return _.get(this.segmentScores, `${this.selectedPriceSheetNumber}`);
  }

}
